import { Account, AccountTypeEnum } from '@/client/accounts';
import { SystemRoles, User } from '@/client/users';
import { PostHogConfig } from 'posthog-js';
import {
  PostHogGlobalProperties,
  PostHogGroupProperties,
  PostHogSnapshotProperties,
} from './posthog.types';

export const posthogConfig = (
  user: User,
): {
  posthogProjectToken?: string;
  posthogOptions: Partial<PostHogConfig>;
} => {
  const allowTracking = user.account.allowPosthogTracking;

  return {
    posthogProjectToken: process.env.REACT_APP_POSTHOG_PROJECT_TOKEN,
    posthogOptions: {
      debug: process.env.REACT_APP_POSTHOG_DEBUG === 'true',
      api_host: process.env.REACT_APP_PUBLIC_POSTHOG_HOST,
      disable_session_recording: !allowTracking,
      opt_in_site_apps: true,
      autocapture: true,
      rageclick: true,
      capture_dead_clicks: true,
      cross_subdomain_cookie: true,

      // #region Disabled features
      // Disabled as it can be noisy and may not provide actionable insights + privacy concerns
      capture_pageview: false,
      capture_heatmaps: false,
      capture_performance: false,
      capture_pageleave: false,
      //#endregion

      before_send: (event) => {
        if (!event) return null;
        return allowTracking ? event : null;
      },
    },
  };
};

export const parseUserType = (user: User) => {
  const userType = user.role?.code;

  switch (userType) {
    case SystemRoles.LEARNER:
      return 'learner';
    case SystemRoles.ADMIN:
    case SystemRoles.MULTIPLE_ACCOUNTS_ADMIN:
      return 'admin';
    case SystemRoles.SUPER_ADMIN:
    case SystemRoles.DEVELOPER:
      return 'superadmin';
    default:
      return 'unknown';
  }
};

export const getAccountType = (account: Account): AccountTypeEnum =>
  account.type ?? AccountTypeEnum.REAL;

export const isFreeTrial = (account: Account): 'yes' | 'no' => {
  if (!account.freeTrialEndsAt) {
    return 'no';
  }

  const now = new Date().getTime();
  const freeTrialDate = new Date(account.freeTrialEndsAt).getTime();

  return freeTrialDate > now ? 'yes' : 'no';
};

export const isTrialRestricted = (account: Account): boolean =>
  isFreeTrial(account) === 'yes' && !account.freeTrialFullAccess;

export const FREE_TRIAL_USER_LIMIT = 5;

export const buildPostHogGroupProperties = (
  account: Account,
): PostHogGroupProperties => ({
  account_id: account.id,
  name: account.name,
  is_free_trial: isFreeTrial(account),
  account_type: getAccountType(account),
  free_trial_expiration_date: account.freeTrialEndsAt
    ? new Date(account.freeTrialEndsAt).toISOString()
    : null,
});

export const buildPostHogGlobalProperties = (
  account: Account,
  user: User,
): PostHogGlobalProperties => ({
  account_id: account.id,
  is_free_trial: isFreeTrial(account),
  user_type: parseUserType(user),
  account_name: account.name,
});

export const buildPostHogSnapshotProperties = (
  account: Account,
): PostHogSnapshotProperties => ({
  logged_in_account_name: account.name,
  is_logged_in_free_trial: isFreeTrial(account),
  account_creation_date: new Date(account.created).toISOString(),
});

export const trackableAccountIds = ['6fb57e71-086f-45d9-a410-58b570b904ce'];
